import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import * as Yup from "yup";
import PropTypes from "prop-types";
import { Formik, Form } from "formik";
import axios from "axios";
import { ArrowBack as ArrowBackIcon } from "@material-ui/icons";
import { Grid, Checkbox, FormControlLabel, Box } from "@material-ui/core";
import { selectUserFullName } from "redux/auth/selectors";
import SrCurrency from "components/common/SrCurrency";
import Amex from "assets/images/paycard/amex.svg";
import Visa from "assets/images/paycard/visa.svg";
import MasterCard from "assets/images/paycard/mastercard.svg";
import useStyles from "./expressChargeStyles";
import { setSuccess } from "../../../../../redux/app/actions/ecommerce";
import translate from "../../../../../i18n/translate";
import { CURRENCY } from "../../../../../constants";
import { PAYMENT, PAYMENT_SEND } from "../../../../../constants/api";
import {
  SrAlert,
  SrButton,
  SrCurrencyInput,
  SrInput,
  SrContentWrapper,
  Typography,
} from "../../../../common";
import { validations } from "utils";

const createPaymentRequest = async (description, amount) => {
  const data = {
    reference: {
      description,
    },
    external: "no required",
    total: {
      amount,
      currency: CURRENCY.MXN,
    },
  };
  try {
    const response = await axios.post(PAYMENT, data);
    return response;
  } catch (error) {
    return error;
  }
};

const sendEmailToUserRequest = async (
  name,
  email,
  token,
  installments = false
) => {
  const data = {
    name,
    email,
    token,
    installments,
  };
  try {
    const response = await axios.post(PAYMENT_SEND, data);
    return response;
  } catch (error) {
    return error;
  }
};

const amountFormat = (amount) => <SrCurrency value={amount} />;

const CreateCardPayment = ({
  onReturn,
  onSubmit: handleReturnOnSubmit,
  isLoading,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const userFullName = useSelector(({ auth }) => selectUserFullName(auth));
  const limitOperation = useSelector(({ auth }) => auth.user?.limitOperation);
  const [error, setError] = useState();

  const initialValues = {
    concept: "",
    amount: "",
    email: "",
    acceptMsi: false,
  };

  const validationSchema = Yup.object().shape({
    concept: Yup.string().required(translate("common.requiredField")),
    amount: validations.amountWithMin.required(
      translate("common.requiredField")
    ),
    email: validations.email.required(translate("common.requiredField")),
  });

  const onSubmit = async (values) => {
    setError();

    const createPaymentResponse = await createPaymentRequest(
      values.concept,
      values.amount
    );

    if (createPaymentResponse?.data?.success) {
      const { token } = createPaymentResponse.data.result;

      const sendEmailToUserResponse = await sendEmailToUserRequest(
        userFullName,
        values.email,
        token,
        values.acceptMsi
      );

      if (sendEmailToUserResponse?.data?.success) {
        dispatch(
          setSuccess({
            title: translate(
              "ecommerce.cobroExpress.createCardPayment.success.title"
            ),
            message: translate(
              "ecommerce.cobroExpress.createCardPayment.success.message",
              {
                amount: <SrCurrency value={values.amount} />,
                email: <div className={classes.breakText}>{values.email}</div>,
              }
            ),
          })
        );

        handleReturnOnSubmit?.();
      } else {
        setError(
          sendEmailToUserResponse?.response?.data?.error?.message ||
            translate("errors.somethingWrong")
        );
      }
    } else {
      setError(
        createPaymentResponse?.response?.data?.error?.message ||
          translate("errors.somethingWrong")
      );
    }
  };

  return (
    <SrContentWrapper isLoading={isLoading}>
      <SrButton
        startIcon={<ArrowBackIcon color="primary" />}
        onClick={onReturn}
      >
        {translate("common.return")}
      </SrButton>
      <Box
        maxWidth={812}
        display="flex"
        flexDirection="column"
        margin="auto"
        marginTop={1}
      >
        <Typography variant="h2" className={classes.headerSpace}>
          {translate("ecommerce.cobroExpress.createCardPayment.heading")}
        </Typography>
        <Typography
          variant="subtitle2SemiBold"
          className={classes.subtitleSpace}
        >
          {translate("ecommerce.cobroExpress.createCardPayment.p1", {
            limitOperation: amountFormat(limitOperation),
          })}
        </Typography>
        {/**
         * // TODO: Add carnet bank image and delete this comment
         */}
        <div className={classes.banksFlexRow}>
          <img src={Amex} alt={translate("banks.amex")} />
          <img
            src={Visa}
            alt={translate("banks.visa")}
            className={classes.spaceCards}
          />
          <img src={MasterCard} alt={translate("banks.mastercard")} />
        </div>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={onSubmit}
        >
          {({
            errors,
            handleBlur,
            handleChange,
            touched,
            values,
            setFieldValue,
            isSubmitting,
          }) => (
            <Form>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <SrInput
                    variant="outlined"
                    label={translate("common.concept")}
                    name="concept"
                    type="text"
                    value={values.concept}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={Boolean(errors.concept && touched.concept)}
                    helperText={touched.concept && errors.concept}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12}>
                  <SrCurrencyInput
                    variant="outlined"
                    label={translate("common.amount")}
                    name="amount"
                    value={values.amount}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={Boolean(errors.amount && touched.amount)}
                    helperText={touched.amount && errors.amount}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12}>
                  <SrInput
                    variant="outlined"
                    label={translate("common.email")}
                    name="email"
                    type="email"
                    value={values.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={Boolean(errors.email && touched.email)}
                    helperText={touched.email && errors.email}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={values.acceptMsi}
                        onChange={(event, value) =>
                          setFieldValue("acceptMsi", value)
                        }
                      />
                    }
                    label={
                      <Typography variant="body2">
                        {translate(
                          `ecommerce.cobroExpress.createCardPayment.acceptMsi`
                        )}
                      </Typography>
                    }
                  />
                </Grid>
                {error && (
                  <Grid item xs={12}>
                    <SrAlert severity="error">{error}</SrAlert>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Box maxWidth="220px">
                    <SrButton
                      variant="contained"
                      fullWidth
                      size="large"
                      type="submit"
                      color="primary"
                      disabled={isSubmitting}
                    >
                      {translate(
                        "ecommerce.cobroExpress.createCardPayment.sendExpressCollection"
                      )}
                    </SrButton>
                  </Box>
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      </Box>
    </SrContentWrapper>
  );
};

CreateCardPayment.propTypes = {
  onReturn: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
};

export default CreateCardPayment;
